import { useState, useCallback } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { sanitizeInput, validateLength } from "@/utils/security";
import { getErrorMessage } from "@/utils/errors";
import { AddScriptForm } from "@/features/scripts/components/AddScriptForm";
import { useCreateScript } from "@/features/scripts/hooks/useCreateScript";

interface AddScriptDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onScriptAdded: () => void;
}

export const AddScriptDialog = ({ open, onOpenChange, onScriptAdded }: AddScriptDialogProps) => {
  const [movieName, setMovieName] = useState("");
  const [customMessage, setCustomMessage] = useState("");
  const [scriptUrl, setScriptUrl] = useState("");
  const [scriptUrlTh, setScriptUrlTh] = useState("");
  const [passwordProtected, setPasswordProtected] = useState(false);

  const { toast } = useToast();
  const { isCreating, createScript } = useCreateScript();

  const resetFormState = useCallback(() => {
    setMovieName("");
    setCustomMessage("");
    setScriptUrl("");
    setScriptUrlTh("");
    setPasswordProtected(false);
  }, []);

  const handleSubmit = useCallback(async () => {
    const sanitizedMovieName = sanitizeInput(movieName);
    const sanitizedCustomMessage = sanitizeInput(customMessage);

    if (!validateLength(sanitizedMovieName, 1, 255)) {
      toast({
        title: "Invalid input",
        description: "Project name must be between 1 and 255 characters.",
        variant: "destructive",
      });
      return;
    }

    if (sanitizedCustomMessage && !validateLength(sanitizedCustomMessage, 0, 1000)) {
      toast({
        title: "Invalid input",
        description: "Custom message must not exceed 1000 characters.",
        variant: "destructive",
      });
      return;
    }

    try {
      await createScript({
        movieName: sanitizedMovieName,
        customMessage: sanitizedCustomMessage,
        scriptUrl,
        scriptUrlTh,
        passwordProtected,
      });

      toast({
        title: "Project added successfully!",
        description: "Your project is ready. Add forms when you're ready to collect feedback.",
      });

      onScriptAdded();
      resetFormState();
      onOpenChange(false);
    } catch (error: unknown) {
      toast({
        title: "Error creating project",
        description: getErrorMessage(error, "Failed to create project"),
        variant: "destructive",
      });
    }
  }, [createScript, customMessage, movieName, onOpenChange, onScriptAdded, passwordProtected, resetFormState, scriptUrl, scriptUrlTh, toast]);

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => {
      if (!nextOpen) {
        resetFormState();
      }
      onOpenChange(nextOpen);
    }}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <AddScriptForm
          values={{
            movieName,
            customMessage,
            scriptUrl,
            scriptUrlTh,
            passwordProtected,
          }}
          onChangeValues={({ movieName: nextMovieName, customMessage: nextCustomMessage, scriptUrl: nextScriptUrl, scriptUrlTh: nextScriptUrlTh, passwordProtected: nextPasswordProtected }) => {
            if (nextMovieName !== undefined) setMovieName(nextMovieName);
            if (nextCustomMessage !== undefined) setCustomMessage(nextCustomMessage);
            if (nextScriptUrl !== undefined) setScriptUrl(nextScriptUrl);
            if (nextScriptUrlTh !== undefined) setScriptUrlTh(nextScriptUrlTh);
            if (nextPasswordProtected !== undefined) setPasswordProtected(nextPasswordProtected);
          }}
          onCancel={() => {
            resetFormState();
            onOpenChange(false);
          }}
          onSubmit={handleSubmit}
          isSubmitting={isCreating}
        />
      </DialogContent>
    </Dialog>
  );
};
